import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatNavList,
  MatListItem,
  MatListItemAvatar,
  MatListItemTitle,
  MatListItemLine,
  MatListItemMeta,
} from '@angular/material/list';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { BrandingService } from '@next/core-lib/branding';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrCurrencyPipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, skipWhile, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { Discount } from '../../models/cycle.model';
import {
  MessageTypeCodeEnum,
  SlaughterReport,
} from '../../models/flock-registration-service.model';
import { FinalSetupNavigationService } from '../../services/final-setup-navigation.service';
import { getSelectedCycle, getSlaughterReports } from '../../store/selectors/cycle.selectors';
import { DiscountItemDialogComponent } from '../discount-item-dialog/discount-item-dialog.component';

import { ImportFromSlaughterReportComponent } from '../import-from-slaughter-report/import-from-slaughter-report.component';
import * as cycleActions from './../../store/actions/cycle.actions';

@Component({
  selector: 'app-discount-overview',
  templateUrl: './discount-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatButton,
    MatIcon,
    MatNavList,
    NgFor,
    MatListItem,
    MatListItemAvatar,
    MatListItemTitle,
    MatListItemLine,
    MatListItemMeta,
    MatSlideToggle,
    MatIconButton,
    MatDivider,
    AsyncPipe,
    TranslatePipe,
    AgrCurrencyPipe,
  ],
})
export class DiscountOverviewComponent implements OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly dialogService = inject(DialogService);
  private readonly localeService = inject(LocaleService);
  private readonly finalSetupNavigationService = inject(FinalSetupNavigationService);
  readonly brandingService = inject(BrandingService);

  private readonly destroyed$ = new Subject<void>();

  cycle$ = this.store.select(getSelectedCycle).pipe(
    takeUntil(this.destroyed$),
    map((cycle) => {
      if (cycle && cycle?.id) {
        const sortedDiscounts = [...(cycle.discounts || [])].sort((a, b) => {
          const applicabilityTypeComparison = a.applicabilityType.localeCompare(
            b.applicabilityType,
          );

          if (applicabilityTypeComparison === 0) {
            return a.label.localeCompare(b.label);
          }

          return applicabilityTypeComparison;
        });
        return {
          ...cycle,
          discounts: sortedDiscounts,
        };
      }
    }),
  );

  slaughterReports$: Observable<SlaughterReport[]> = this.store.select(getSlaughterReports).pipe(
    skipWhile((sr) => sr === null || sr === undefined),
    take(1),
    withLatestFrom(this.cycle$),
    map(([s, cycle]) =>
      s?.filter(
        (sr) =>
          !sr.linkedCycleId &&
          !sr.linkedBarnId &&
          new Date(sr.technicalResult.dateTime) >
            new Date(
              Math.min.apply(
                null,
                cycle.dayOldChicks.map((doc) => new Date(doc.arrivalDate)),
              ),
            ) &&
          sr.header.type === MessageTypeCodeEnum.AfterPayment,
      ),
    ),
  );
  isFinalSetup$: Observable<boolean> = this.finalSetupNavigationService.canNavigate();

  isAdvisor: boolean;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const store = this.store;

    this.store
      .pipe(select(AuthorizationSelectors.hasAtLeastAdvisorRole), take(1))
      .subscribe((hasAtLeastAdvisorRole) => {
        this.isAdvisor = hasAtLeastAdvisorRole;
      });
    this.cycle$
      .pipe(
        skipWhile((c) => !c?.id),
        take(1),
      )
      .subscribe((c) => {
        store.dispatch(
          cycleActions.loadSlaughterReportsRequest({
            fromDate: c.cycleDate,
          }),
        );
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  add() {
    if (this.isAdvisor) {
      this.cycle$.pipe(take(1)).subscribe((cycle) => {
        this.router.navigate(['/cycle', cycle.id, 'orders', 'add']);
        this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
          if (isMobile) {
            this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
          }
        });
      });
    }
  }

  addDialog() {
    if (this.isAdvisor) {
      this.store.dispatch(
        cycleActions.setSelectedDiscountId({
          id: undefined,
        }),
      );
      this.cycle$
        .pipe(
          skipWhile((c) => !c?.id),
          take(1),
        )
        .subscribe((cycle) => {
          this.dialogService
            .form(DiscountItemDialogComponent, {
              confirmButton: true,
              confirmButtonText: this.localeService.translate('add'),
              pushHistoryState: false,
              discountId: null,
            })
            .subscribe((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                const nextOrderId =
                  cycle.discounts && cycle.discounts.length > 0
                    ? Math.max(...cycle.discounts.map((d) => d.id)) + 1
                    : 1;
                this.store.dispatch(
                  cycleActions.addDiscount({
                    cycleId: cycle.id,
                    discount: { ...result.result.discount, id: nextOrderId },
                  }),
                );
              }
            });
        });
    }
  }

  import() {
    this.cycle$
      .pipe(withLatestFrom(this.slaughterReports$), take(1))
      .subscribe(([cycle, slaughterReports]) => {
        this.dialogService.fullscreenDialog(
          ImportFromSlaughterReportComponent,
          {
            cycle,
            slaughterReports,
            fullscreen: true,
          },
          {
            maxWidth: 'auto',
          },
        );
      });
  }

  openDiscountEdit(discountId: number) {
    if (this.isAdvisor) {
      this.store.dispatch(
        cycleActions.setSelectedDiscountId({
          id: discountId,
        }),
      );
      this.cycle$.pipe(take(1)).subscribe((cycle) => {
        this.dialogService
          .form(DiscountItemDialogComponent, {
            confirmButton: !cycle.discounts.find((d) => d.id === discountId)
              .linkedSlaughterReportId,
            confirmButtonText: this.localeService.translate('save'),
            pushHistoryState: false,
            discountId,
          })
          .subscribe((result) => {
            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.store.dispatch(
                cycleActions.editDiscount({ cycleId: cycle.id, discount: result.result.discount }),
              );
            }
            this.store.dispatch(
              cycleActions.setSelectedDiscountId({
                id: undefined,
              }),
            );
          });
      });
    }
  }

  delete(event: Event, discount: Discount) {
    if (this.isAdvisor) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.cycle$.pipe(take(1)).subscribe((cycle) => {
        if (discount && discount.id) {
          this.store.dispatch(
            cycleActions.deleteDiscountWithConfirmation({ cycleId: cycle.id, id: discount.id }),
          );
        }
      });
    }
  }

  onToggleShowInFinalSetup(discount: Discount) {
    const newDiscount = { ...discount, showInFinalSetup: !discount.showInFinalSetup };

    this.cycle$.pipe(take(1)).subscribe((cycle) => {
      if (newDiscount && newDiscount.id) {
        this.store.dispatch(
          cycleActions.editDiscount({ cycleId: cycle.id, discount: newDiscount }),
        );
      }
    });
  }
}
